import { useEffect, useRef, useState } from "react";

import { getInboxStreamTicket, inboxStreamUrl } from "api/api.service";

const RECONNECT_STEPS_MS = [1000, 2000, 5000, 10000, 20000];

/**
 * The inbox's live connection, the way the Wingzz CRM inbox does it: one
 * Server-Sent Events stream, small events ({ conversationId, kind }) and the
 * page re-fetches only what changed. New messages, AI replies, "AI is
 * replying…", delivery ticks and takeovers appear without a refresh.
 *
 * EventSource cannot send the Authorization header, so each connection first
 * asks for a single-use, 60-second ticket and opens the stream with that (a
 * session token in the URL would end up in access logs). Because a ticket
 * works once, the browser's own auto-reconnect (same URL) cannot be used:
 * every drop closes the stream and reconnects here with a fresh ticket, on a
 * backoff, until it is back. A 401/403 on the ticket (access revoked) stops it.
 *
 * Returns whether the stream is connected, so the page can poll less while it is.
 */
export default function useInboxStream(onEvent) {
  const [connected, setConnected] = useState(false);
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.EventSource === "undefined") return undefined;
    let cancelled = false;
    let source = null;
    let timer = null;
    let failures = 0;

    const reconnect = () => {
      if (cancelled) return;
      clearTimeout(timer);
      timer = setTimeout(connect, RECONNECT_STEPS_MS[Math.min(failures, RECONNECT_STEPS_MS.length - 1)]);
      failures += 1;
    };

    async function connect() {
      if (cancelled) return;
      let ticket;
      try {
        ticket = (await getInboxStreamTicket())?.data?.ticket;
      } catch (err) {
        if (err?.Status === 401 || err?.Status === 403) return;
        reconnect();
        return;
      }
      if (cancelled || !ticket) return;

      const stream = new window.EventSource(inboxStreamUrl(ticket));
      source = stream;
      stream.onopen = () => {
        failures = 0;
        setConnected(true);
      };
      stream.onmessage = (e) => {
        let event;
        try {
          event = JSON.parse(e.data);
        } catch {
          return;
        }
        if (event?.kind === "ready") return;
        onEventRef.current?.(event);
      };
      stream.onerror = () => {
        if (stream !== source) return;
        stream.close();
        source = null;
        setConnected(false);
        reconnect();
      };
    }

    connect();
    return () => {
      cancelled = true;
      clearTimeout(timer);
      source?.close();
      source = null;
    };
  }, []);

  return { connected };
}
